import { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { dispatch } from "../../state/store";
import { setUsersRefresh } from "../../state/ducks";

import { alignItems, strCode } from "../../consts";
import { t } from "../../services";
import { withSection } from "../../hoc";

import { useFetchGroupAssignments, useFetchGroups, useFetchUsers } from "../../apiHooks";
import userInsightsUtils from "../../utils/userInsightsUtils";
import { csvUtil } from "../../utils";

import {
  FlexContainers,
  Heading5,
  PageContainer,
  RefreshButton,
  SearchInput,
  SmallButton,
  SVG,
  WrapperContainer
} from "../../components";
import {
  ExportInsightsModal,
  UserInsightsFilters,
  UserInsightsTable,
  UserStatistics
} from "../../containers";
import { FilterButton, FilterCounter } from "../../containers/UserManagement/UsersTable/styled";
import { Controls, Header, LastSegmentedDateInfo } from "./styled";

const UserInsightsPage = () => {
  const [searchedText, setSearchedText] = useState("");

  const [userInsightsData, setUserInsightsData] = useState([]);
  const [userInsightsFilteredData, setUserInsightsFilteredData] = useState([]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const [exportModalActive, setExportModalActive] = useState(false);

  const userInsightsLoading = false;

  const lastSegmentedDate = "12/12/2024 16:45";

  const filters = useSelector((store) => store.userInsights?.userInsightsFilters);

  const {
    fetchCallback: fetchUsers,
    data: users,
    loading: usersLoading,
    refresh: usersRefresh
  } = useFetchUsers();

  const { data: userGroups, fetchCallback: fetchGroups } = useFetchGroups();
  const { data: groupAssignments, fetchCallback: fetchGroupAssignments } =
    useFetchGroupAssignments();

  useEffect(() => {
    setUserInsightsData(userInsightsUtils.getUserSegments(users));
  }, [users]);

  useEffect(() => {
    fetchUsers(false);
    fetchGroups(false);
  }, []);

  useEffect(() => {
    if (userGroups?.length) {
      fetchGroupAssignments(userGroups?.map((g) => g.id));
    }
  }, [userGroups]);

  useEffect(() => {
    if (usersRefresh) {
      fetchUsers(true);
      fetchGroups(true);
    }
  }, [usersRefresh]);

  useEffect(() => {
    setUserInsightsFilteredData(userInsightsUtils.filterUserInsights(userInsightsData, filters));
  }, [userInsightsData, filters]);

  const onRerunSegmentation = () => {
    console.log("onRerunSegmentation");
  };

  const onExport = (includeFilters) => {
    const data = includeFilters ? userInsightsFilteredData : userInsightsData;
    const formattedIsights = userInsightsUtils.formatInsightsForExport(data, groupAssignments);

    const dataCsv = csvUtil.createCSVFile(formattedIsights);
    csvUtil.downloadCSVFile(dataCsv, t(strCode.USER_INSIGHTS_NAV_BTN));
  };

  const handleExport = () => {
    if (activeFilters > 0) {
      setExportModalActive(true);
    } else {
      onExport(false);
    }
  };

  return (
    <PageContainer alignItems={alignItems.start} pt={0}>
      <Header Container={FlexContainers.FlexRowStart} width="100%" gap={16} pb={16}>
        <WrapperContainer Container={FlexContainers.FlexRowStart} width="100%" gap={16}>
          <Heading5>{t(strCode.USER_INSIGHTS_NAV_BTN)}</Heading5>

          <RefreshButton isLoading={false} handleRefresh={() => dispatch(setUsersRefresh(true))} />
        </WrapperContainer>

        {lastSegmentedDate && (
          <LastSegmentedDateInfo>
            {t(strCode.LAST_SEGMENTED_INFO, { date: lastSegmentedDate })}
          </LastSegmentedDateInfo>
        )}
      </Header>

      <UserStatistics
        userInsightsData={userInsightsData ?? []}
        lastSegmentedDate={lastSegmentedDate}
        loading={usersLoading}
      />

      <Controls
        Container={FlexContainers.FlexColumn}
        gap={12}
        width={"100%"}
        pb={filtersVisible ? 0 : 16}>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={16} width={"100%"}>
          <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
            <WrapperContainer width={340}>
              <SearchInput
                id="user-insights-search"
                value={searchedText}
                setValue={setSearchedText}
                placeholder={t(strCode.SEARCH_PLACEHOLDER)}
                disabled={usersLoading}
              />
            </WrapperContainer>

            <FilterButton
              active={filtersVisible}
              onClick={() => setFiltersVisible((prev) => !prev)}>
              <SVG.TuneIcon />
              {activeFilters > 0 && <FilterCounter>{activeFilters}</FilterCounter>}
            </FilterButton>
          </WrapperContainer>

          <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
            <SmallButton secondary onClick={onRerunSegmentation} disabled={usersLoading}>
              {t(strCode.RERUN_SEGMENTATION_BTN)}
            </SmallButton>

            <SmallButton onClick={handleExport} disabled={usersLoading}>
              {t(strCode.EXPORT_BTN)}
            </SmallButton>
          </WrapperContainer>
        </WrapperContainer>

        <UserInsightsFilters visible={filtersVisible} setActiveFilters={setActiveFilters} />
      </Controls>

      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        <UserInsightsTable
          userInsightsData={userInsightsFilteredData ?? []}
          searchedText={searchedText}
          loading={usersLoading}
          error={!!userInsightsLoading}
        />
      </WrapperContainer>

      <ExportInsightsModal
        active={exportModalActive}
        setActive={setExportModalActive}
        onExport={onExport}
      />
    </PageContainer>
  );
};

export default withSection(UserInsightsPage);
